/**
 * `<BillingSummary>` — the "Billing / Coding Summary (Coder Review)" grid at the tail of
 * the note's Plan section:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │  Billing / Coding Summary (Coder Review)     │
 *   │  ──────────────────────────────────────────  │
 *   │  CPT Candidates    [99214] 99213  99215   ✎  │   ← chip group
 *   │  Place of Service  POS 10 (Patient Home …)   │
 *   │  Modifier          95 (Telehealth)           │
 *   │  Add-On Code       G2211 (Longitudinal …)    │
 *   │  Note              Final E/M code selection …│
 *   └──────────────────────────────────────────────┘
 *
 * Everything shown comes from `visitBillingGet`, where the Claim decides the codes and the
 * note's extension supplies the AI's ranked alternatives and rationale, so this grid, the
 * editor behind the pencil and the Billing page can never disagree about the same visit.
 *
 * The AI proposes; the doctor decides. Each candidate is a chip with the code's display
 * name and per-code reasoning on hover. Clicking one makes it the visit's primary E/M —
 * written to the Claim right away through the same save the editor uses, re-priced from
 * the quote. While the visit has no billed line yet the click opens the editor instead,
 * where the diagnoses the Claim needs are picked. The `×` drops a candidate the doctor
 * does not want hanging around; the selected one cannot be dropped — pick another first.
 */
import { useMemo, useState } from 'react'
import { ActionIcon, Box, Divider, Group, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { IconPencil } from '@tabler/icons-react'
import type { BillingCandidate } from '@/pages/Patient/components/ProgressNoteEdit/progressNoteEditSchema'
import { CPT_SYSTEM } from '@/pages/Patient/components/ProgressNoteEdit/fhirSystems'
import {
  MONO_FONT,
  PROGRESS_NOTE_CORAL as CORAL,
} from '@/pages/Patient/components/ProgressNoteEdit/tokens'
import { useTocAnchor } from '@/pages/Patient/components/ProgressNoteViewer/tocRegistry'
import { BillingModal } from './BillingModal'
import {
  diagnosesNeedingReview,
  hasUnavailableDiagnoses,
  removeCandidate,
  useVisitBilling,
} from './useVisitBilling'

export interface BillingSummaryProps {
  encounterId: string
  /**
   * Whether the doctor may change the visit's billing here: select a candidate, drop one,
   * open the editor. The locked-note viewer passes `true` at every Claim status (PD-1689).
   */
  editable?: boolean
  /**
   * Awaited before the editor opens, so it reads a Claim that already holds the note's
   * pending billing edits (PD-2016). Resolving `false` keeps the editor closed.
   */
  beforeEditorOpen?: () => Promise<unknown>
}

// Lightweight POS labels (CMS Place of Service code set). Telehealth-leaning
// values are the only ones the AI emits today; the rest fall back to "Unknown"
// so unfamiliar codes still render legibly without breaking the layout.
const POS_LABELS: Record<string, string> = {
  '02': 'Telehealth — Other than Patient Home',
  '10': 'Patient Home — Telehealth',
  '11': 'Office',
  '12': 'Home',
  '17': 'Walk-in Retail Health Clinic',
  '20': 'Urgent Care Facility',
  '22': 'On-campus Outpatient Hospital',
  '23': 'Emergency Room — Hospital',
}

// Common telehealth-related modifier annotations. Anything outside this list
// renders without an annotation.
const MODIFIER_ANNOTATIONS: Record<string, string> = {
  '95': 'Telehealth',
  GT: 'Telehealth — Real-Time',
  GQ: 'Telehealth — Store-and-Forward',
}

function formatModifierList(modifiers: string[]): string {
  if (modifiers.length === 0) return '—'
  return modifiers
    .map((m) => {
      const annotation = MODIFIER_ANNOTATIONS[m]
      return annotation ? `${m} (${annotation})` : m
    })
    .join(', ')
}

function formatAddOnList(addOnCodes: Array<{ code: string; display: string }>): {
  codes: string
  helper?: string
} {
  if (addOnCodes.length === 0) return { codes: '—' }
  return {
    codes: addOnCodes.map((c) => c.code).join(', '),
    helper: addOnCodes.map((c) => c.display).join(' · '),
  }
}

/**
 * Tooltip body for a candidate chip. The first line is the code + display so
 * the coder sees what they're picking; the body paragraph is the AI's
 * per-candidate `reasoning`. Multi-line layout via `whiteSpace: pre-line`.
 */
function buildCandidateTooltip(candidate: BillingCandidate): string {
  const header = candidate.display ? `${candidate.code} — ${candidate.display}` : candidate.code
  const reasoning = candidate.reasoning?.trim() || 'No per-code rationale provided.'
  return `${header}\n\n${reasoning}`
}

function CandidateChip({
  candidate,
  selected,
  onClick,
  onDelete,
}: {
  candidate: BillingCandidate
  selected: boolean
  /** Absent when the viewer may not change the billing — the chip then only informs. */
  onClick?: () => void
  /**
   * Optional remove-this-candidate handler. When provided AND the chip is
   * NOT selected, renders a small inline `×` icon to the right of the code.
   * Suppressed for the selected chip — you can't drop the active billing
   * code; the doctor has to pick a different one first.
   */
  onDelete?: () => void
}) {
  // Container is a flex row so the code stays click-to-select while the
  // `×` icon (when shown) is its own clickable region — we stopPropagation
  // on the icon so clicking it doesn't ALSO select the chip.
  return (
    <Tooltip
      label={buildCandidateTooltip(candidate)}
      multiline
      w={320}
      withArrow
      position="top"
      openDelay={150}
      styles={{
        tooltip: {
          whiteSpace: 'pre-line',
          lineHeight: 1.45,
          fontSize: 12,
          padding: '8px 12px',
        },
      }}
    >
      <div
        data-testid={`billing-candidate-${candidate.code}`}
        data-selected={selected ? 'true' : 'false'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '3px 8px 3px 10px',
          gap: 4,
          borderRadius: 6,
          fontFamily: MONO_FONT,
          fontSize: 12,
          fontWeight: 700,
          background: selected ? CORAL : '#f3f4f6',
          color: selected ? '#fff' : '#374151',
          border: `1px solid ${selected ? CORAL : '#e5e7eb'}`,
          transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
        }}
      >
        <UnstyledButton
          type="button"
          onClick={onClick}
          disabled={!onClick}
          style={{
            color: 'inherit',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            cursor: onClick ? 'pointer' : 'default',
          }}
        >
          {candidate.code}
        </UnstyledButton>
        {onDelete && !selected && (
          <UnstyledButton
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            aria-label={`Remove ${candidate.code} from AI suggestions`}
            data-testid={`billing-candidate-${candidate.code}-delete`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 14,
              height: 14,
              borderRadius: 7,
              color: '#9ca3af',
              fontSize: 14,
              lineHeight: 1,
              cursor: 'pointer',
              transition: 'color 120ms ease, background 120ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#dc2626'
              e.currentTarget.style.background = '#fee2e2'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9ca3af'
              e.currentTarget.style.background = 'transparent'
            }}
          >
            ×
          </UnstyledButton>
        )}
      </div>
    </Tooltip>
  )
}

function Row({
  label,
  children,
  valueStyle,
}: {
  label: string
  children: React.ReactNode
  valueStyle?: React.CSSProperties
}) {
  return (
    <>
      <Text style={{ color: '#9ca3af', fontWeight: 600, fontSize: 13 }}>{label}</Text>
      <Box style={{ color: '#111', fontSize: 13, ...valueStyle }}>{children}</Box>
    </>
  )
}

export function BillingSummary({
  encounterId,
  editable = false,
  beforeEditorOpen,
}: BillingSummaryProps) {
  // Registered directly rather than through `<NoteHeading>`: this heading runs
  // its own 13px scale inside the billing card.
  useTocAnchor('billing-coding')

  const billing = useVisitBilling(encounterId)
  const [modalOpen, setModalOpen] = useState(false)
  const [opening, setOpening] = useState(false)

  const openEditor = async () => {
    setOpening(true)
    let ready: unknown
    try {
      ready = await beforeEditorOpen?.()
    } finally {
      setOpening(false)
    }
    if (ready === false) return
    setModalOpen(true)
  }

  const suggestion = billing.suggestion
  // Sort defensively by rank so rank-1 always renders first even if the
  // candidates came back out of order.
  const candidates = useMemo(
    () => [...(suggestion?.candidates ?? [])].sort((a, b) => a.rank - b.rank),
    [suggestion],
  )
  const selected =
    candidates.find((candidate) => candidate.rank === suggestion?.selectedRank) ?? candidates[0]
  // The chip and the candidate delete save `billing.entries` as they are, and the server
  // drops a diagnosis it cannot express (PD-1958): only Edit billing may write until the
  // biller removes or replaces it.
  const dxBlocked = hasUnavailableDiagnoses(billing.data)
  const reviewCodes = diagnosesNeedingReview(billing.entries, suggestion?.diagnoses)
  const canWrite = editable && !billing.isSaving && !dxBlocked

  const handleSelect = async (candidate: BillingCandidate) => {
    if (!canWrite || !suggestion || candidate.rank === selected?.rank) return

    // Nothing billed yet: the Claim's first line needs diagnoses, which only the editor
    // can supply — so the pick is made there.
    const [primary, ...addOns] = billing.entries
    if (!primary) {
      await openEditor()
      return
    }

    // A different E/M level is a different charge (PD-1597): re-price the line from the
    // quote when it has a price for the new code. Modifiers, diagnoses and quantity stay.
    const price = billing.quote.priceFor(candidate.code)
    const nextPrimary = {
      ...primary,
      procedure: { system: CPT_SYSTEM, code: candidate.code, display: candidate.display ?? '' },
      ...(price != null ? { price } : {}),
    }

    // The error notification comes from the save.
    await billing
      .save({
        entries: [nextPrimary, ...addOns],
        suggestion: { ...suggestion, selectedRank: candidate.rank },
      })
      .catch(() => undefined)
  }

  const handleDeleteCandidate = async (rank: number) => {
    if (!canWrite || !suggestion) return

    await billing
      .save({ entries: billing.entries, suggestion: removeCandidate(suggestion, rank) })
      .catch(() => undefined)
  }

  const posCode = suggestion?.placeOfService || ''
  const posLabel = POS_LABELS[posCode]
  const posValue = posCode ? (posLabel ? `POS ${posCode} (${posLabel})` : `POS ${posCode}`) : '—'

  const modifierValue = formatModifierList(suggestion?.modifiers || [])

  const addOn = formatAddOnList(suggestion?.addOnCodes || [])
  const addOnValue = addOn.helper ? `${addOn.codes} (${addOn.helper})` : addOn.codes

  const note = suggestion?.reasoning?.trim() || '—'

  // Dropping a candidate rides on the lines PATCH, which the server refuses without a
  // billed line — so the affordance waits until there is one.
  const canDelete = editable && !dxBlocked && billing.entries.length > 0

  return (
    <Box
      mt="lg"
      mb="xs"
      style={{
        padding: '16px 20px',
        background: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
      }}
      data-testid="plan-billing-grid"
    >
      <Text fw={700} c="#374151" mb={10} style={{ fontSize: 13 }} id="billing-coding">
        Billing / Coding Summary (Coder Review)
      </Text>
      {editable && dxBlocked && (
        <Text size="xs" c="orange.8" mb={10} data-testid="billing-summary-dx-blocked">
          A diagnosis on this visit's billing is unavailable. Open Edit billing to remove or replace
          it — until then, billing changes from the note are not saved.
        </Text>
      )}
      {reviewCodes.length > 0 && (
        <Text size="xs" c="orange.8" mb={10} data-testid="billing-summary-dx-review">
          {reviewCodes.length === 1
            ? `1 diagnosis needs review: ${reviewCodes[0]}. Open Edit billing to confirm or replace it.`
            : `${reviewCodes.length} diagnoses need review: ${reviewCodes.join(', ')}. Open Edit billing to confirm or replace them.`}
        </Text>
      )}
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr',
          rowGap: 8,
          columnGap: 16,
          alignItems: 'center',
        }}
      >
        <Row label="CPT Candidates:">
          <Group gap={14} wrap="nowrap" align="center">
            {candidates.length === 0 ? (
              <Text style={{ fontFamily: MONO_FONT, color: '#111' }}>—</Text>
            ) : (
              <Group gap={6} wrap="wrap" data-testid="billing-candidates-chips">
                {candidates.map((candidate) => (
                  <CandidateChip
                    key={`${candidate.rank}-${candidate.code}`}
                    candidate={candidate}
                    selected={selected?.rank === candidate.rank}
                    onClick={editable ? () => void handleSelect(candidate) : undefined}
                    onDelete={
                      canDelete ? () => void handleDeleteCandidate(candidate.rank) : undefined
                    }
                  />
                ))}
              </Group>
            )}
            {editable && (
              <>
                {/* Vertical separator: visually separates the AI-suggested
                    chip group (passive — selection) from the explicit edit
                    affordance (active — opens modal). Sized to match the
                    chip height so it reads as a divider, not a fence. */}
                <Divider
                  orientation="vertical"
                  style={{
                    height: 22,
                    borderColor: '#d1d5db',
                    flexShrink: 0,
                  }}
                />
                <Tooltip label="Edit billing" withArrow position="top" openDelay={120}>
                  <ActionIcon
                    variant="default"
                    size="md"
                    radius="md"
                    onClick={() => void openEditor()}
                    loading={opening}
                    aria-label="Edit billing"
                    data-testid="plan-billing-grid-edit"
                    styles={{
                      root: {
                        // Match chip footprint visually: 28×28 with a clean
                        // white-button look (Mantine's `default` variant gives
                        // a subtle border + white bg). Icon in slate-700 so
                        // the pencil reads clearly without competing with
                        // the coral chip-selection.
                        flexShrink: 0,
                        color: '#334155',
                      },
                    }}
                  >
                    <IconPencil size={14} stroke={2} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        </Row>
        <Row label="Place of Service:">
          <Text style={{ fontSize: 13 }}>{posValue}</Text>
        </Row>
        <Row label="Modifier:">
          <Text style={{ fontFamily: MONO_FONT, fontSize: 13 }}>{modifierValue}</Text>
        </Row>
        <Row label="Add-On Code:">
          <Text style={{ fontFamily: MONO_FONT, fontSize: 13 }}>{addOnValue}</Text>
        </Row>
        <Row label="Note:">
          <Text style={{ color: '#6b7280', fontStyle: 'italic', fontSize: 13 }}>{note}</Text>
        </Row>
      </Box>

      {editable && (
        <BillingModal
          encounterId={encounterId}
          opened={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </Box>
  )
}
